import { Button } from '@/components/shared/button/Button';
import { TSTitleCard } from '@/components/shared/card/Card';
import { ConfirmDialog } from '@/components/shared/modal/ConfirmDialog';
import { handleMutationResult } from '@/helpers/toastHelper';
import { SkillType } from '@/services/tasks/skillSettings.types';
import { useUpdateSkills } from '@/services/tasks/useSkills';
import { FunctionComponent, useState } from 'react';
import { IoClose, IoRefresh } from 'react-icons/io5';
import { useNavigate } from 'react-router-dom';
import adminStrings from '../../admin.translations';

interface AdminDeactivateSkillCardProps {
  skill: SkillType;
}

const config = [
  // false = inactive
  {
    title: adminStrings.skillsSection.deactivateReactivate.reactivate,
    icon: IoRefresh,
    info: adminStrings.skillsSection.deactivateReactivate.reactivateInfo,
    buttonText: adminStrings.common.reactivate,
    confirmTitle: adminStrings.skillsSection.deactivateReactivate.confirmReactivateTitle,
    confirmBody: adminStrings.skillsSection.deactivateReactivate.confirmReactivateBody,
    successMessage: adminStrings.skillsSection.deactivateReactivate.successReactivate,
    errorMessage: adminStrings.skillsSection.deactivateReactivate.errorReactivate
  },
  // true = active
  {
    title: adminStrings.skillsSection.deactivateReactivate.deactivate,
    icon: IoClose,
    info: adminStrings.skillsSection.deactivateReactivate.deactivateInfo,
    buttonText: adminStrings.common.deactivate,
    confirmTitle: adminStrings.skillsSection.deactivateReactivate.confirmTitle,
    confirmBody: adminStrings.skillsSection.deactivateReactivate.confirmDeactivateBody,
    successMessage: adminStrings.skillsSection.deactivateReactivate.successDeactivate,
    errorMessage: adminStrings.skillsSection.deactivateReactivate.errorDeactivate
  }
] as const;

const AdminDeactivateReactivateSkillCard: FunctionComponent<AdminDeactivateSkillCardProps> = ({ skill }) => {
  const { title, icon, info, buttonText, confirmTitle, confirmBody, successMessage, errorMessage } =
    config[+skill.isActive];

  const { fetching, update } = useUpdateSkills();
  const navigate = useNavigate();

  const onSubmit = async () => {
    const result = await update([{ ...skill, isActive: !skill.isActive }]);

    await handleMutationResult(result, {
      successMessage,
      errorMessage,
      onSuccess: () => navigate('/admin/skills')
    });
  };

  const [isModalOpen, setIsModalOpen] = useState(false);

  return (
    <TSTitleCard title={title(skill.name)} icon={icon} className="w-full">
      <div className="flex flex-row items-center space-x-3">
        <Button onClick={() => setIsModalOpen(true)} loading={fetching} danger tint>
          {buttonText}
        </Button>
        <p>{info}</p>
      </div>
      <ConfirmDialog
        onConfirm={onSubmit}
        open={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        title={confirmTitle(skill.name)}
        confirmLabel={buttonText}
        cancelLabel={adminStrings.common.cancel}
      >
        {confirmBody}
      </ConfirmDialog>
    </TSTitleCard>
  );
};

export default AdminDeactivateReactivateSkillCard;
